import type { MeResponse, MembershipSummary, Role, TenantSettings } from '@convometry/contracts';
import { roleAtLeast } from '@convometry/contracts';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { api, ApiError } from '../lib/api.js';
import { qk } from '../lib/query.js';

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api('me'),
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof ApiError && err.kind === 'unauthorized') && count < 2,
  });
}

export interface TenantContextValue {
  tenantId: string;
  membership: MembershipSummary;
  me: MeResponse;
  settings: TenantSettings;
  timeZone: string;
  role: Role;
  can: (required: Role) => boolean;
}

const TenantContext = React.createContext<TenantContextValue | null>(null);
export const TenantProvider = TenantContext.Provider;

export function useTenant(): TenantContextValue {
  const ctx = React.useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a tenant route');
  return ctx;
}

export function makeCan(role: Role) {
  return (required: Role) => roleAtLeast(role, required);
}
